import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import CategoriesRankingGraphicCard from '../components/panel/CategoriesRankingGraphicCard';
import Container from "@mui/material/Container";
import CustomButton from '../components/CustomButton';
import CustomContainer from "../components/CustomContainer";
import CustomTextField from "../components/CustomTextField";
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import GeneratedEconomyCard from "../components/panel/GeneratedEconomyCard";
import NumberOfUsesCard from "../components/panel/NumberOfUsesCard";
import Pagination from '@mui/material/Pagination';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ClearIcon from '@mui/icons-material/Clear';
import Stack from "@mui/material/Stack";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from "@mui/material/Typography";

import { dateTimeFormatter } from '../utils';

import { fetchResume, fetchTransactionsHistory } from '../services';

export default function Transactions() {

  // ESTADOS DA APLICAÇÃO

  const [transactionsHistory, setTransactionsHistory] = useState([]);
  const [transactionsAmount, setTransactionsAmount] = useState([]);
  const [page, setPage] = useState(1);
  const [resume, setResume] = useState({});
  const [initialDate, setInitialDate] = useState('');
  const [finalDate, setFinalDate] = useState('');

  // GETS

  // ----- resumo das informações
  const getResume = async () => {
    const resumeFetched = await fetchResume();
    setResume(resumeFetched);
  };

  // ----- histórico de transações
  const getTransactionsHistory = async () => {
    const history = await fetchTransactionsHistory(page, initialDate, finalDate);
    setTransactionsHistory(history.history);
    setTransactionsAmount(history.amount);
  };

  // VALIDAÇÂO DE INPUTS

  // ----- filtro de período
  const isDateInputsValid = () => {
    const starts = new Date(initialDate);
    const ends = new Date(finalDate);

    if (!initialDate || !finalDate || ends < starts) return false;
    return true;
  };

  // FILTRO POR PERÍODO

  // ----- submit do filtro
  const submitDateFilter = async (event) => {
    event.preventDefault();

    if (isDateInputsValid()) {
      if (page === 1) {
        getTransactionsHistory();
      } else {
        await setPage(1);
      }
    }
  };

  // ----- reset do filtro
  const handleResetFilterButton = () => {
    setInitialDate('');
    setFinalDate('');
    setPage(1);
  };

  // PAGINAÇÃO DAS TRANSAÇÕES

  // ----- clique nos botões de paginação
  const handlePagination = async (_event, value) => {
    await setPage(value);
  };

  // USE EFFECTS

  // ----- quando a página carrega
  useEffect(() => {
    getResume();
  }, []);

  // ----- quando a paginação é alerada
  useEffect(() => {
    getTransactionsHistory();
  }, [page])

    // ----- quando a data é resetada
    useEffect(() => {
      if (!initialDate && !finalDate) getTransactionsHistory();
    }, [initialDate, finalDate])

  // RETORNO
  return (
    <Container sx={{ minWidth: 1100 }}>
      <Stack spacing={3} mb={3}>

        { /* CARDS DE RESUMO DAS INFORMAÇÕES */ }
        <Container>
          <Stack direction="row" spacing={3}>
            <NumberOfUsesCard resume={ resume } />
            <GeneratedEconomyCard resume={ resume } />
            <CategoriesRankingGraphicCard resume={ resume } />
          </Stack>
        </Container>

        { /* CONTAINER PRINCIPAL */ }
        <CustomContainer>

          { /* TÍTULO E FILTRO */ }
          <Box
            alignItems="center"
            display="flex"
            justifyContent="space-between"
            px={10}
          >
            { /* TÍTULO DA PÁGINA */ }
            <Typography
              color="primary"
              component="h2"
              my={3}
              sx={{
                alignItems: "center",
                display: "flex",
              }}
              variant="h4"
            >
              <ReceiptIcon fontSize="md" sx={{ mr: 2 }} />
              Histórico de interações
            </Typography>

            { /* FILTRO POR PERÍODO */ }
            <form onSubmit={ submitDateFilter }>
              <CustomTextField
                color="primary"
                InputLabelProps={{ shrink: true }}
                label="De"
                onChange={ ({ target: { value } }) => setInitialDate(value) }
                required
                size="small"
                sx={{ mr: 1, width: "180px" }}
                type="date"
                value={ initialDate }
                variant="outlined"
              />
              <CustomTextField
                color="primary"
                InputLabelProps={{ shrink: true }}
                label="Até"
                onChange={ ({ target: { value } }) => setFinalDate(value) }
                required
                size="small"
                sx={{ mr: 1, width: "180px" }}
                type="date"
                value={ finalDate }
                variant="outlined"
              />
              <Tooltip title="Filtrar">
                <CustomButton
                  color="primary"
                  sx={{ maxWidth: "40px", minWidth: "40px", mr: 1 }}
                  type="submit"
                  variant="contained"
                >
                  <FilterAltIcon />
                </CustomButton>
              </Tooltip>
              <Tooltip title="Descartar filtro">
                <CustomButton
                  color="error"
                  onClick={ handleResetFilterButton }
                  sx={{ maxWidth: "40px", minWidth: "40px" }}
                  type="button"
                  variant="contained"
                >
                  <ClearIcon />
                </CustomButton>
              </Tooltip>
            </form>
          </Box>

          { /* TABELA COM TRANSAÇÕES */ }
          <Table aria-label="simple table">

            { /* CABEÇAÇHO */ }
            <TableHead>
              <TableRow>
                <TableCell align="center">Data - Hora</TableCell>
                <TableCell align="center">CPF</TableCell>
                <TableCell align="center">Tipo de Interação</TableCell>
                <TableCell align="center">Valor Economizado</TableCell>
                <TableCell align="center">Estabelecimento</TableCell>
                <TableCell align="center">Categoria</TableCell>
              </TableRow>
            </TableHead>

            { /* CORPO */ }
            <TableBody>
              {
                !!transactionsHistory
                && transactionsHistory.map(({ datetime, cpf, interactionType, economy, establishmentName, category }, index) => (
                  <TableRow
                    key={index}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell align="center" component="th" scope="row">
                      { dateTimeFormatter(datetime) }
                    </TableCell>
                    <TableCell align="center">
                      { `${cpf.substring(0, 3)}.${cpf.substring(3, 6)}.${cpf.substring(6, 9)}-${cpf.substring(9)}` }
                    </TableCell>
                    <TableCell align="center">
                      { interactionType.charAt(0).toUpperCase() + interactionType.slice(1) }
                    </TableCell>
                    <TableCell align="center">
                      { !!economy ? `R$ ${economy.replace('.', ',')}` : 'N/A' }
                    </TableCell>
                    <TableCell align="center">
                      { establishmentName }
                    </TableCell>
                    <TableCell align="center">
                      {
                        category
                          .split(' ')
                          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(' ')
                      }
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>

          { /* PAGINAÇÃO DAS TRANSAÇÕES */ }
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1, mt: 2, maxWidth: "100%" }}>
            { !!transactionsAmount && <Pagination
              color="primary"
              count={ Math.ceil(transactionsAmount/10) }
              onChange={handlePagination}
              page={page}
              size="small"
            /> }
          </Box>
        </CustomContainer>
      </Stack>
    </Container>
  );
};
